import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Receipt, FileText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EditExpenseDialog } from "@/components/AdminEdit";
import { AdminDeleteButton } from "@/components/AdminActions";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";

const bulkEntry = (r) => r.kind === "proof"
  ? [`p:${r.req_id}:${r.index}`, { kind: "proofs", id: r.req_id, key: String(r.index) }]
  : [`g:${r.id}`, { kind: "general-expenses", id: r.id }];

export default function AllExpenses() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const bulk = useBulk();

  const load = useCallback(() => {
    api.get("/admin/expenses").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [load]);

  const s = q.trim().toLowerCase();
  const filtered = rows.filter((r) => !s || [r.requester_name, r.description, r.plate_number, r.source, r.branch_name]
    .some((x) => (x || "").toLowerCase().includes(s)));
  const total = filtered.reduce((t, r) => t + (r.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Receipt className="h-6 w-6 text-brand" /> Semua Pengeluaran
          </h2>
          <p className="text-sm text-muted-foreground">Seluruh bukti pengeluaran dari pengajuan kas & pengeluaran umum mandiri — {filtered.length} data · {formatRupiah(total)}</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pemohon, keterangan, plat..." className="max-w-xs h-9" data-testid="all-expenses-search" />
      </div>
      <BulkBar bulk={bulk} onDone={load} label="pengeluaran" />
      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-2.5">{[0, 1, 2].map((i) => <div key={i} className="h-4 rounded-md skeleton-shimmer" />)}</div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center" data-testid="all-expenses-empty">Belum ada pengeluaran.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="all-expenses-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="pl-4 py-2.5 w-8"><BulkCheckAll bulk={bulk} entries={filtered.map(bulkEntry)} testId="bulk-expense-all" /></th>
                  <th className="text-left px-4 py-2.5">Tanggal</th><th className="text-left px-4 py-2.5">Pemohon</th>
                  <th className="text-left px-4 py-2.5">Keterangan</th><th className="text-left px-4 py-2.5">Sumber</th>
                  <th className="text-right px-4 py-2.5">Nominal</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const key = r.kind === "proof" ? `${r.req_id}-${r.index}` : r.id;
                  const url = r.kind === "proof" ? `/admin/cash-requests/${r.req_id}/proofs/${r.index}` : `/admin/general-expenses/${r.id}`;
                  const history = r.kind === "proof"
                    ? { entityType: "cash_request", entityId: r.req_id, filterPrefix: `Bukti #${r.index + 1} ` }
                    : { entityType: "general_expense", entityId: r.id };
                  return (
                    <tr key={key} className="border-t" data-testid={`all-expense-row-${i}`}>
                      <td className="pl-4 py-2.5"><BulkCheck bulk={bulk} k={bulkEntry(r)[0]} item={bulkEntry(r)[1]} testId={`bulk-expense-${i}`} /></td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(r.uploaded_at)}</td>
                      <td className="px-4 py-2.5">{r.requester_name}<br /><span className="text-xs text-muted-foreground">{r.branch_name}</span></td>
                      <td className="px-4 py-2.5">{r.description}{r.plate_number && <span className="block text-xs font-mono text-muted-foreground">{r.plate_number}</span>}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {r.kind === "proof" ? <Link to={`/requests/${r.req_id}`} className="text-brand hover:underline">{r.source}</Link> : r.source}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.amount)}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          {r.file_id && <a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-brand"><FileText className="h-4 w-4" /></a>}
                          <EditExpenseDialog item={r} url={url} vehicles={vehicles} onDone={load} testId={`edit-all-expense-${i}`} history={history} />
                          <AdminDeleteButton path={r.kind === "proof" ? `/admin/sub/proofs/${r.req_id}/${r.index}` : `/admin/records/general-expenses/${r.id}`} onDone={load} iconOnly testId={`del-all-expense-${i}`}
                            confirmText="Pengeluaran akan dihapus permanen. Saldo kas pemohon kembali seperti sebelum pengeluaran ini dicatat." />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
